"use client";

import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { Zap, Copy, Shield, Bell, ArrowRight } from "lucide-react";

/** FrenFlow's Telegram copytrading bot. */
const BOT_URL = "https://t.me/FrenFlowBot";
const TG_BLUE = "#229ED9";

const FEATURES = [
  { icon: Copy, title: "One-tap copy", body: "Pick a verified trader and mirror their positions from chat." },
  { icon: Zap, title: "Sub-second execution", body: "Copies fire the second a leader opens a position — sub-second." },
  { icon: Bell, title: "Live alerts", body: "Every trade, fill and PnL update pushed straight to Telegram." },
  { icon: Shield, title: "Self-custody", body: "Your keys, your funds. The bot never holds your money." },
];

/** Official Telegram paper-plane glyph. */
function TelegramGlyph({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z" />
    </svg>
  );
}

/** Telegram double-check (read receipt). */
function ReadTicks() {
  return (
    <svg viewBox="0 0 16 11" className="w-3.5 h-3 inline-block" fill="none" aria-hidden>
      <path d="M1 5.5L4 8.5L9.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M6 8.2L6.6 8.8L12 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Three-dot "typing…" bubble. */
function Typing() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{ duration: 0.2 }}
      className="flex justify-start"
    >
      <div className="rounded-[12px] rounded-bl-[4px] bg-white dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A] px-3 py-2.5 flex items-center gap-1">
        {[0, 1, 2].map((i) => (
          <motion.span
            key={i}
            className="w-1.5 h-1.5 rounded-full bg-gray-400 dark:bg-gray-500"
            animate={{ opacity: [0.3, 1, 0.3], y: [0, -2, 0] }}
            transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.15 }}
          />
        ))}
      </div>
    </motion.div>
  );
}

const bubble = {
  initial: { opacity: 0, y: 10, scale: 0.97 },
  animate: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.32, ease: [0.2, 0.65, 0.3, 1] as [number, number, number, number] } },
  exit: { opacity: 0, transition: { duration: 0.2 } },
};

/** Small timestamp shown bottom-right of a bubble. */
function Stamp({ children, light = false }: { children: React.ReactNode; light?: boolean }) {
  return (
    <span className={`ml-2 inline-flex items-center gap-0.5 text-[10px] align-bottom ${light ? "text-white/70" : "text-gray-400 dark:text-gray-500"}`}>
      {children}
    </span>
  );
}

/**
 * TelegramCopytrading — dedicated section for the @FrenFlowBot copytrading bot.
 * Left: positioning + feature bullets + CTA. Right: an ANIMATED Telegram chat
 * that loops a full copy flow (/follow → ack → trade copied → resolved + PnL)
 * while in view. Messages mirror the bot's real formats; only staging animates.
 */
export default function TelegramCopytrading() {
  const chatRef = useRef<HTMLDivElement>(null);
  const inView = useInView(chatRef, { amount: 0.4 });
  // 0 reset · 1 cmd · 2 typing · 3 ack · 4 typing · 5 copy · 6 typing · 7 resolved/PnL · 8 hold
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const delays = [500, 1100, 900, 1300, 900, 1700, 950, 4800, 600];
    let current = 0;
    setStep(0);
    const run = () => {
      if (cancelled) return;
      timer = setTimeout(() => {
        current = (current + 1) % delays.length;
        setStep(current);
        run();
      }, delays[current]);
    };
    run();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [inView]);

  return (
    <section className="py-20 lg:py-28 bg-white dark:bg-[#0a0a0b]">
      <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left — copy */}
          <div>
            <div className="inline-flex items-center gap-1.5 font-inter-tight text-[11px] font-semibold uppercase tracking-widest mb-3" style={{ color: TG_BLUE }}>
              <TelegramGlyph className="w-3.5 h-3.5" />
              Telegram Bot
            </div>
            <h2 className="font-inter-tight font-semibold text-gray-900 dark:text-white tracking-[-0.02em] text-[34px] lg:text-[48px] leading-[1.05]">
              Copytrade Polymarket from Telegram
            </h2>
            <p className="mt-4 max-w-xl text-gray-600 dark:text-gray-400 font-inter-tight text-base lg:text-lg">
              Follow proven Polymarket traders and mirror every position straight from chat —
              no app to install, and your keys never leave your wallet.
            </p>

            <div className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-6 max-w-xl">
              {FEATURES.map((f) => (
                <div key={f.title} className="flex gap-3">
                  <f.icon className="w-5 h-5 text-[#00B37A] dark:text-[#00FF9D] flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-inter-tight font-semibold text-[15px] text-gray-900 dark:text-white">
                      {f.title}
                    </div>
                    <div className="mt-0.5 text-sm text-gray-600 dark:text-gray-400">{f.body}</div>
                  </div>
                </div>
              ))}
            </div>

            <a
              href={BOT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group mt-10 inline-flex items-center gap-2 px-6 py-3.5 rounded-[5px] text-white font-inter-tight font-medium text-[15px] transition-opacity hover:opacity-90"
              style={{ backgroundColor: TG_BLUE }}
            >
              <TelegramGlyph className="w-4 h-4" />
              Open @FrenFlowBot
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </a>
          </div>

          {/* Right — animated Telegram chat */}
          <div
            ref={chatRef}
            className="rounded-[14px] border border-gray-200 dark:border-[#1A1A1A] bg-gray-50 dark:bg-[#0f0f12] overflow-hidden shadow-[0_8px_40px_-12px_rgba(0,0,0,0.18)] dark:shadow-[0_8px_40px_-12px_rgba(0,0,0,0.6)]"
          >
            {/* chat header */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-[#1A1A1A] bg-white dark:bg-[#18181D]">
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-white" style={{ backgroundColor: TG_BLUE }}>
                <TelegramGlyph className="w-4 h-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-inter-tight font-semibold text-[14px] text-gray-900 dark:text-white leading-tight">
                  FrenFlow Bot
                </div>
                <div className="flex items-center gap-1.5 leading-tight">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60 animate-ping" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  </span>
                  <span className="text-[11px] text-[#00B37A] dark:text-[#00FF9D]">online</span>
                </div>
              </div>
              <TelegramGlyph className="w-4 h-4 text-gray-300 dark:text-gray-600" />
            </div>

            {/* messages — bot's REAL formats; only the reveal is animated.
                follow.ts (`✅ Now copying X!`) + notification-service.ts
                (`🔥 Copied from X …`) + resolution payout. Numbers are a
                consistent illustrative example (50 → 119.0476 sh @ $0.42 →
                $1 payout = $119.05, +$69.05 / +138%). */}
            <div className="p-4 space-y-2.5 text-[13px] leading-snug min-h-[360px]">
              <AnimatePresence mode="popLayout">
                {step >= 1 && (
                  <motion.div key="cmd" {...bubble} className="flex justify-end">
                    <div className="rounded-[12px] rounded-br-[4px] px-3 py-2 font-inter-tight font-medium text-white" style={{ backgroundColor: TG_BLUE }}>
                      /follow YoshiValue
                      <Stamp light><span>11:42</span><ReadTicks /></Stamp>
                    </div>
                  </motion.div>
                )}

                {step === 2 && <Typing key="typing1" />}

                {step >= 3 && (
                  <motion.div key="ack" {...bubble} className="flex justify-start">
                    <div className="max-w-[88%] rounded-[12px] rounded-bl-[4px] bg-white dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A] text-gray-900 dark:text-gray-100 px-3 py-2">
                      <div className="flex items-center gap-1.5 font-semibold">
                        ✅ Now copying <span className="font-semibold">YoshiValue</span>!
                      </div>
                      <div className="mt-1 text-gray-500 dark:text-gray-400">
                        New trades will be mirrored automatically.
                      </div>
                    </div>
                  </motion.div>
                )}

                {step === 4 && <Typing key="typing2" />}

                {step >= 5 && (
                  <motion.div key="copy" {...bubble} className="flex justify-start">
                    <div className="max-w-[88%] rounded-[12px] rounded-bl-[4px] bg-white dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A] text-gray-900 dark:text-gray-100 px-3 py-2 font-mono text-[12px]">
                      <div className="font-semibold">🔥 Copied from YoshiValue</div>
                      <div className="mt-1">🎯 <span className="text-gray-500 dark:text-gray-400">Outcome:</span> Yes @ $0.42</div>
                      <div>📦 <span className="text-gray-500 dark:text-gray-400">Shares Bought:</span> 119.0476</div>
                      <div className="pl-3 text-gray-500 dark:text-gray-400">└ 💸 Spent: $50.00</div>
                    </div>
                  </motion.div>
                )}

                {step === 6 && <Typing key="typing3" />}

                {step >= 7 && (
                  <motion.div
                    key="pnl"
                    initial={{ opacity: 0, y: 10, scale: 0.97, boxShadow: "0 0 0 0px rgba(16,185,129,0)" }}
                    animate={{
                      opacity: 1, y: 0, scale: 1,
                      boxShadow: ["0 0 0 3px rgba(16,185,129,0.35)", "0 0 0 0px rgba(16,185,129,0)"],
                      transition: { duration: 0.5, ease: [0.2, 0.65, 0.3, 1] as [number, number, number, number] },
                    }}
                    exit={{ opacity: 0, transition: { duration: 0.2 } }}
                    className="flex justify-start"
                  >
                    <div className="max-w-[88%] rounded-[12px] rounded-bl-[4px] bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 text-gray-900 dark:text-gray-100 px-3 py-2 font-mono text-[12px]">
                      <div className="font-semibold">✅ Market resolved: YES</div>
                      <div className="mt-1">💰 <span className="text-gray-500 dark:text-gray-400">Payout:</span> $119.05</div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        📈 <span className="text-gray-500 dark:text-gray-400">PnL:</span>
                        <span className="font-bold text-emerald-600 dark:text-emerald-400">+$69.05</span>
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">+138%</span>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
